import { useCallback } from 'react';
import { useTour } from '@kognitos/lattice';
import { useGateValue } from '@statsig/react-bindings';
import { FEATURE_FLAGS } from '@/shared/types/features';
import { isTourCompleted, resetTourCompletion } from '../persistence';
import { useTourAnalytics } from './use-tour-analytics';
import {
  useTourStore,
  selectActiveTourId,
  selectSetActiveTour,
} from '../tours.store';
import { isE2ETestMode } from '@/shared/auth/utils/e2e';
import type { ITourConfig, IUseManagedTourReturn } from '../types';

/**
 * Enhanced tour hook that wraps lattice's useTour with persistence gating,
 * feature flag gating, analytics tracking on start, and centralized state management.
 *
 * All tours are gated behind the ENABLE_ONBOARDING_TOURS feature flag by default.
 * Individual tours can add additional gating with their own featureGate.
 *
 * Must be used inside a ManagedTourProvider (which wraps TourProvider).
 * The provider handles onComplete/onSkip analytics, step tracking, and i18n.
 * This hook is for consumers who want to programmatically start/control tours.
 *
 * @param tourConfig - The complete tour configuration
 * @returns Managed tour state and actions
 */
export function useManagedTour(tourConfig: ITourConfig): IUseManagedTourReturn {
  const tour = useTour();
  const { trackStarted } = useTourAnalytics(
    tourConfig.id,
    tourConfig.analytics,
    tourConfig.steps,
  );

  const activeTourId = useTourStore(selectActiveTourId);
  const setActiveTour = useTourStore(selectSetActiveTour);

  const isCompleted = isTourCompleted(tourConfig.id, tourConfig.persistence);

  // Get the global tour feature gate
  const globalTourFeatureGate = FEATURE_FLAGS.ENABLE_ONBOARDING_TOURS;
  const isGlobalTourFeatureGateOpen = useGateValue(globalTourFeatureGate);

  // Get the feature gate for the tour
  const isTourFeatureGateOpen = useGateValue(
    tourConfig.featureGate ?? globalTourFeatureGate,
  );

  // Enabled if:
  // - Tour is enabled
  // - Not in E2E test mode
  // - Global tour feature gate is open
  // - Tour feature gate is open
  const isEnabled =
    tourConfig.enabled !== false &&
    !isE2ETestMode() &&
    isGlobalTourFeatureGateOpen &&
    isTourFeatureGateOpen;

  const startTour = useCallback(() => {
    if (!isEnabled) return;
    if (isCompleted) return;
    if (activeTourId && activeTourId !== tourConfig.id) return;

    setActiveTour(tourConfig.id);
    tour.startTour();
    trackStarted();
  }, [
    isEnabled,
    isCompleted,
    activeTourId,
    tourConfig.id,
    setActiveTour,
    tour,
    trackStarted,
  ]);

  const resetTour = useCallback(() => {
    resetTourCompletion(tourConfig.id, tourConfig.persistence);
  }, [tourConfig.id, tourConfig.persistence]);

  return {
    startTour,
    endTour: tour.endTour,
    isActive: tour.isActive,
    currentStep: tour.currentStep,
    totalSteps: tour.totalSteps,
    isCompleted,
    resetTour,
  };
}
